import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Upload, Trash2, Film, X, Plus, AlertTriangle } from '../icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getWeddings, getWeddingMedia, uploadWeddingMedia, deleteWeddingMedia } from '../services/api';
import { useConfirm } from '../components/Modal';
import mediaUrl from '../utils/mediaUrl';

function MediaGallery() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const fileInputRef = useRef(null);
  const [weddingId, setWeddingId] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'image', 'video'
  const [canWrite, setCanWrite] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Items whose file is gone: flagged by the server (file_missing) or found when the <img>/<video> fails
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const markBroken = (id) => setBrokenIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  const isMissing = (item) => item.file_missing || brokenIds.has(item.id);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Only couples and delegates can access media
    if (user.role === 'helper' && user.collaborator?.collaborator_role !== 'delegate') {
      navigate('/checklist');
      return;
    }

    setLoadError(false);
    getWeddings()
      .then((res) => {
        if (res.data.length > 0) {
          const w = res.data[0];
          setWeddingId(w.id);
          setWedding(w);
          // Same rule as the server: the couple and read/write delegates may upload and delete
          setCanWrite(w.user_id === user.id || user.collaborator?.access_level === 'write');
          return getWeddingMedia(w.id).then(r => setMedia(r.data));
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [user, navigate, authLoading, reloadKey]);

  const loadMedia = async (wId) => {
    try {
      const res = await getWeddingMedia(wId);
      setMedia(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: t('coupleTools.loadFailed') });
    }
  };

  // Server-side limits (routes/weddings.js): 50 MB per file, 10 files per request, these extensions
  const MAX_MB = 50;
  const BATCH = 10;
  const ALLOWED_EXT = /\.(jpe?g|png|gif|webp|mp4|mov|avi|webm)$/i;

  const uploadErrorText = (err) => {
    const code = err.response?.data?.code;
    if (code === 'file_too_large' || err.response?.status === 413) return t('media.errTooLarge');
    if (code === 'unsupported_type') return t('media.errType');
    if (code === 'too_many_files') return t('media.errTooMany');
    return err.response?.data?.error || t('media.uploadFailed');
  };

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (picked.length === 0) return;

    // Check files here so one bad file doesn't sink the whole batch with a bare error
    const problems = [];
    const valid = picked.filter((f) => {
      if (!ALLOWED_EXT.test(f.name)) { problems.push(t('media.skippedType').replace('{name}', f.name)); return false; }
      if (f.size > MAX_MB * 1024 * 1024) { problems.push(t('media.skippedSize').replace('{name}', f.name)); return false; }
      return true;
    });

    setUploading(true);
    setMessage(null);
    let uploaded = 0;
    for (let i = 0; i < valid.length; i += BATCH) {
      const batch = valid.slice(i, i + BATCH);
      const formData = new FormData();
      batch.forEach(f => formData.append('media', f));
      try {
        const res = await uploadWeddingMedia(weddingId, formData);
        uploaded += Array.isArray(res.data) ? res.data.length : batch.length;
      } catch (err) {
        const reason = uploadErrorText(err);
        problems.push(`${batch.map(f => f.name).join(', ')}: ${reason}`);
      }
    }
    if (uploaded > 0) await loadMedia(weddingId);
    setUploading(false);

    if (problems.length === 0) {
      setMessage({ type: 'success', text: `${uploaded} ${t('media.uploadSuccess')}` });
    } else {
      setMessage({
        type: uploaded > 0 ? 'warning' : 'error',
        text: uploaded > 0 ? `${uploaded} ${t('media.uploadSuccess')} ${t('media.someSkipped')}` : t('media.nothingUploaded'),
        details: problems,
      });
    }
  };

  const handleDelete = async (mediaId) => {
    if (!await confirmDialog({ title: t('media.deleteMedia'), message: t('media.deleteConfirm'), danger: true, confirmText: t('common.delete') })) return;
    try {
      await deleteWeddingMedia(weddingId, mediaId);
      await loadMedia(weddingId);
      setSelectedMedia(null);
      setMessage({ type: 'success', text: t('media.mediaDeleted') });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('media.deleteFailed') });
    }
  };

  const filteredMedia = filter === 'all' ? media : media.filter(m => m.media_type === filter);
  const imageCount = media.filter(m => m.media_type === 'image').length;
  const videoCount = media.filter(m => m.media_type === 'video').length;

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (loadError || !wedding) {
    return (
      <div className="helpers-page">
        <div className="container">
          <div className="empty-state-sm">
            <Image size={48} />
            <h2>{loadError ? t('coupleTools.loadFailed') : t('media.noWeddingFound')}</h2>
            {!loadError && <p>{t('media.noWeddingDesc')}</p>}
            <div style={{ marginTop: 16 }}>
              {loadError ? (
                <button className="btn btn-primary" onClick={() => { setLoading(true); setReloadKey(k => k + 1); }}>{t('coupleTools.tryAgain')}</button>
              ) : user?.role === 'couple' && (
                <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>{t('coupleTools.goToDashboard')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="helpers-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1><Image size={28} /> {t('media.title')}</h1>
            <p>{t('media.subtitle')} {wedding.partner1_name} & {wedding.partner2_name}</p>
          </div>
          <div style={{ display: canWrite ? undefined : 'none' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              multiple
              accept="image/*,video/*"
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></div> {t('media.uploading')}</>
              ) : (
                <><Upload size={16} /> {t('media.uploadMedia')}</>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {!canWrite && <div className="alert alert-warning">{t('coupleTools.readOnlyNotice')}</div>}
        {message && (
          <div className={`alert alert-${message.type}`}>
            <div>
              {message.text}
              {message.details?.length > 0 && (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {message.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
            <button className="alert-close" onClick={() => setMessage(null)}>&times;</button>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="category-tabs" style={{ marginBottom: '20px' }}>
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
            {t('media.allTab')} ({media.length})
          </button>
          <button className={filter === 'image' ? 'active' : ''} onClick={() => setFilter('image')}>
            <Image size={14} /> {t('media.photosTab')} ({imageCount})
          </button>
          <button className={filter === 'video' ? 'active' : ''} onClick={() => setFilter('video')}>
            <Film size={14} /> {t('media.videosTab')} ({videoCount})
          </button>
        </div>

        {/* Gallery Grid */}
        {filteredMedia.length === 0 ? (
          <div className="empty-state-sm" style={{ padding: '60px 20px' }}>
            <Image size={48} />
            <h3>{t('media.noMediaYet')}</h3>
            {canWrite && <p>{t('media.noMediaDesc')}</p>}
            {canWrite && (
              <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
                <Plus size={16} /> {t('media.uploadFirst')}
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '16px'
          }}>
            {filteredMedia.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => { setSelectedMedia(item); setLightboxIndex(idx); }}
                style={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  aspectRatio: '1',
                  backgroundColor: '#f3f4f6',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'; }}
              >
                {isMissing(item) ? (
                  <div className="media-missing-tile">
                    <AlertTriangle size={28} />
                    <strong>{t('media.fileMissing')}</strong>
                    <span>{canWrite ? t('media.fileMissingHint') : t('media.fileMissingHintReadOnly')}</span>
                    {canWrite && (
                      <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                        <Trash2 size={14} /> {t('common.delete')}
                      </button>
                    )}
                  </div>
                ) : item.media_type === 'image' ? (
                  <img
                    src={mediaUrl(item.file_url)}
                    alt={item.caption || item.file_name}
                    onError={() => markBroken(item.id)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2937' }}>
                    <Film size={48} color="#9ca3af" />
                  </div>
                )}
                {item.media_type === 'video' && (
                  <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                    <Film size={12} /> {t('media.video')}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', padding: '20px 10px 10px', color: '#fff', fontSize: '12px' }}>
                  {item.file_name && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file_name}</div>}
                  {item.first_name && <div style={{ opacity: 0.8 }}>{t('media.by')} {item.first_name}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Swiper-Powered Lightbox */}
        {selectedMedia && (
          <div
            className="gallery-lightbox"
            onClick={() => setSelectedMedia(null)}
          >
            <div className="lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
              {canWrite && (
                <button
                  onClick={() => handleDelete(selectedMedia.id)}
                  className="lightbox-btn lightbox-btn-danger"
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
              <button
                onClick={() => setSelectedMedia(null)}
                className="lightbox-btn"
              >
                <X size={16} /> {t('common.close')}
              </button>
            </div>
            <div onClick={(e) => e.stopPropagation()} style={{ width: '95vw', height: '85vh' }}>
              <Swiper
                modules={[Navigation, Pagination, Zoom, Keyboard]}
                navigation
                pagination={{ clickable: true, type: 'fraction' }}
                zoom={{ maxRatio: 4 }}
                keyboard={{ enabled: true }}
                initialSlide={lightboxIndex}
                spaceBetween={0}
                slidesPerView={1}
                onSlideChange={(swiper) => {
                  const item = filteredMedia[swiper.activeIndex];
                  if (item) setSelectedMedia(item);
                }}
                className="lightbox-swiper"
              >
                {filteredMedia.map((item) => (
                  <SwiperSlide key={`lb-${item.id}`}>
                    {isMissing(item) ? (
                      <div className="media-missing-lightbox">
                        <AlertTriangle size={40} />
                        <strong>{t('media.fileMissing')}</strong>
                        <span>{item.file_name}</span>
                        <span>{canWrite ? t('media.fileMissingHint') : t('media.fileMissingHintReadOnly')}</span>
                      </div>
                    ) : item.media_type === 'image' ? (
                      <div className="swiper-zoom-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '85vh' }}>
                        <img
                          src={mediaUrl(item.file_url)}
                          onError={() => markBroken(item.id)}
                          alt={item.caption || item.file_name}
                          style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px' }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '85vh' }}>
                        <video
                          src={mediaUrl(item.file_url)}
                          onError={() => markBroken(item.id)}
                          controls
                          autoPlay
                          style={{ maxWidth: '95%', maxHeight: '80vh', borderRadius: '8px' }}
                        />
                      </div>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            {(selectedMedia.caption || selectedMedia.file_name) && (
              <div className="lightbox-info">
                {selectedMedia.caption || selectedMedia.file_name}
                {selectedMedia.first_name && <span> — {t('media.uploadedBy')} {selectedMedia.first_name} {selectedMedia.last_name}</span>}
              </div>
            )}
          </div>
        )}
      </div>
      {ConfirmDialog}
    </div>
  );
}

export default MediaGallery;
